// src/features/task/create/TaskQuickCreateDrawer.tsx
// Filing a task without leaving the list.
//
// The full page (/tasks/new) is for considered work. This is the other half of
// the same idea: a narrow panel beside the list, for the task you thought of
// while looking at the list. The list is the point — it stays readable, stays
// clickable, and is never dimmed, because seeing what already exists is what
// stops the same task being filed twice, and what makes the parent obvious.
//
// So this is a parallel panel, not a modal: no backdrop, no focus trap, no
// scroll lock. Focus moves in when it opens and back to the trigger when it
// closes, and Escape closes it — but only when focus is actually inside it, so
// a pill popover gets its own Escape first.
//
// It shows the compact form: the key the task will get, the title, the
// description, and one wrapping row of property pills. It deliberately does
// *not* reproduce the full page's always-open properties panel — that is the
// difference between the two surfaces. Anything this panel cannot say fits on
// the full form, one quiet link away, carrying what is already typed through
// the shared sessionStorage draft.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';

import { Alert, Button, ErrorAlert } from '@react/components';
import type { Task } from '@core/types/task';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { TagPicker } from '@/components/TagPicker';
import { UserPicker } from '@/components/UserPicker';
import { AutoGrowTextarea, TitleInput } from '@/components/form';
import {
  getAvatarColor,
  getInitials,
  getPriorityColor,
  getPriorityIconPath,
  taskPriorityOptions,
} from '@/features/task/logic';

import { AddAction, CreateSection } from './AddAction';
import { AttachmentDropzone } from './AttachmentDropzone';
import { ChecklistDraftEditor } from './ChecklistDraftEditor';
import { CustomFieldControl } from './CustomFieldControl';
import { CREATE_ICONS, Icon } from './icons';
import { PillOptionList, PropertyPill } from './PropertyPill';
import { TaskTypeStrip } from './TaskTypeStrip';
import { customFieldId, isCustomFieldEmpty, typeDefIconPath, typeDefId } from './logic';
import { parentForScope, type ParentKind, type QuickCreateScope } from './scope';
import {
  SECTION_ATTACHMENTS,
  SECTION_CHECKLISTS,
  SECTION_WATCHERS,
  customFieldSectionKey,
  useTaskCreateForm,
  type TaskCreateForm,
} from './useTaskCreateForm';

/** DOM id of the drawer's title field — its own, so both surfaces can coexist. */
const TITLE_FIELD_ID = 'task-quick-create-title';

export interface TaskQuickCreateDrawerProps {
  /** The scope the list is showing — where the default parent comes from. */
  scope?: QuickCreateScope | null;
  /** Close the panel. The caller unmounts it; the draft survives in storage. */
  onClose: () => void;
  /** A task now exists. The list updates in place — nobody navigates. */
  onCreated: (created: Task | null, createdId: string) => void;
}

export const TaskQuickCreateDrawer = observer(
  ({ scope, onClose, onCreated }: TaskQuickCreateDrawerProps) => {
    const { t } = useI18n();
    const panelRef = useRef<HTMLElement>(null);
    /** Where focus was when the panel opened — where it goes back to. */
    const returnFocusRef = useRef<HTMLElement | null>(null);
    const [justCreated, setJustCreated] = useState('');
    const [confirmClose, setConfirmClose] = useState(false);

    const initialParent = useMemo(() => parentForScope(scope), [scope]);

    /** Lets `onCreated` clear the very form it is handed by. */
    const formHandle = useRef<TaskCreateForm | null>(null);

    const f = useTaskCreateForm({
      initialParent,
      titleFieldId: TITLE_FIELD_ID,
      // Staying is the whole point of this surface. The list behind already
      // shows the new task, so navigating to it would throw away the context
      // that made filing here worthwhile — and people file in runs.
      onCreated: (created, createdId) => {
        onCreated(created, createdId);
        // Name what was filed. The server's title is the truth; the typed one
        // is the fallback for a response that carries only an id.
        setJustCreated(created?.title || formHandle.current?.form.title.trim() || '');
        formHandle.current?.discardDraft();
        window.requestAnimationFrame(() => {
          document.getElementById(TITLE_FIELD_ID)?.focus();
        });
      },
    });
    formHandle.current = f;

    const {
      store,
      descriptionRef,
      formRef,
      parentKind,
      setParentKind,
      parentId,
      setParentId,
      parentType,
      setParent,
      parentKinds,
      parentOptions,
      parentIsLoading,
      parentLoadError,
      retryParents,
      parentValue,
      parentDisplayName,
      pickerScope,
      form,
      setField,
      setAssignee,
      errors,
      errorText,
      isSubmitting,
      submit,
      submitNow,
      canSubmit,
      missingRequired,
      availableTypes,
      selectedType,
      setSelectedTypeId,
      typeLabel,
      capabilities,
      priorityValue,
      priorityIcon,
      priorityIconClass,
      tags,
      setTags,
      tagsValue,
      formatDate,
      contextLoading,
      contextError,
      retryContext,
      selectedTemplateId,
      applyTemplate,
      templateDropped,
      numberingScheme,
      keyPreview,
      customFieldGroups,
      cfValues,
      setCustomField,
      filledInGroup,
      checklists,
      setChecklists,
      checklistItemCount,
      watchers,
      setWatchers,
      pendingFiles,
      setPendingFiles,
      handleAddFiles,
      uploadWarning,
      sectionOpen,
      toggleSection,
      isDirty,
      draftRestored,
      setDraftRestored,
      discardDraft,
      flushDraft,
      touched,
    } = f;

    // ── Parent follows the list ──────────────────────────────────────────
    // Switching the workspace scope while the panel is open changes what the
    // person is looking at, so it changes where the task would go — unless
    // they have already picked a parent themselves.
    const scopeKey = `${initialParent.kind}:${initialParent.id}`;
    const lastScopeKey = useRef(scopeKey);
    useEffect(() => {
      if (lastScopeKey.current === scopeKey) return;
      lastScopeKey.current = scopeKey;
      if (touched.current.has('parent')) return;
      setParent(initialParent);
    }, [scopeKey, initialParent, setParent, touched]);

    // ── Focus in, focus back ─────────────────────────────────────────────
    // The title field focuses itself on mount (`initialFocus`); this returns
    // focus to whatever opened the panel once it goes away.
    useEffect(() => {
      const opener = document.activeElement;
      returnFocusRef.current = opener instanceof HTMLElement ? opener : null;
      return () => {
        const target = returnFocusRef.current;
        if (target && document.body.contains(target)) target.focus();
      };
    }, []);

    /** Close, keeping whatever is typed: the draft is written before leaving. */
    const closeKeepingDraft = useCallback(() => {
      flushDraft();
      setConfirmClose(false);
      onClose();
    }, [flushDraft, onClose]);

    /** Ask first when there is something in the form; just close when empty. */
    const requestClose = useCallback(() => {
      if (isDirty) setConfirmClose(true);
      else closeKeepingDraft();
    }, [isDirty, closeKeepingDraft]);

    // Escape closes — but only when focus is inside the panel. A pill popover
    // portals itself out of here and owns Escape while it is open.
    useEffect(() => {
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          if (confirmClose) return;
          const active = document.activeElement;
          if (!active || !panelRef.current?.contains(active)) return;
          e.preventDefault();
          requestClose();
          return;
        }
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
          const active = document.activeElement;
          if (!active || !panelRef.current?.contains(active)) return;
          e.preventDefault();
          void submitNow();
        }
      };
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
    }, [requestClose, submitNow, confirmClose]);

    // Typing again means the last confirmation is stale news.
    useEffect(() => {
      if (isDirty && justCreated) setJustCreated('');
    }, [isDirty, justCreated]);

    // ── The full form, carrying what is already typed ─────────────────────
    // Same draft key (product + parent), so the page picks the draft up as it
    // loads; `flushDraft` beats the debounce to the click.
    const fullFormHref = useMemo(() => {
      const params = new URLSearchParams();
      if (parentKind !== 'none') {
        params.set('parent_type', parentKind === 'entity' ? parentType : parentKind);
        if (parentId) params.set('parent_id', parentId);
      }
      if (selectedTemplateId) params.set('template', selectedTemplateId);
      const qs = params.toString();
      return `/tasks/new${qs ? `?${qs}` : ''}`;
    }, [parentKind, parentType, parentId, selectedTemplateId]);

    const notSet = t('common.notSet');

    const inputClass =
      'w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground ' +
      'focus:outline-none focus:ring-2 focus:ring-ring';

    const clearButtonClass =
      'self-start rounded-sm text-xs text-muted-foreground underline-offset-2 ' +
      'hover:text-destructive hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';

    /**
     * Custom-field groups that hold a required field. The rest live on the full
     * form: a compact panel should not grow a settings screen — but a required
     * field that cannot be filled here would be a dead end, and a dead end is
     * worse than a long panel.
     */
    const requiredCustomFieldGroups = customFieldGroups.filter((group) =>
      group.fields.some((def) => def.is_required),
    );

    // ── Property pills ───────────────────────────────────────────────────
    // One wrapping row, in the order people ask: what is it, how urgent, who,
    // when, how big, what is it about, where does it live.

    const typePill = (
      <PropertyPill
        icon={typeDefIconPath(selectedType)}
        swatch={selectedType?.color || undefined}
        label={t('task.createForm.pill.type')}
        value={typeLabel(selectedType)}
        disabled={isSubmitting}
        popoverTitle={t('task.field.type')}
        popoverWidth={300}
      >
        {(close) => (
          <TaskTypeStrip
            types={availableTypes}
            value={typeDefId(selectedType)}
            onChange={(id) => {
              touched.current.add('type');
              setSelectedTypeId(id);
              close();
            }}
            isLoading={contextLoading}
            error={contextError}
            onRetry={retryContext}
            disabled={isSubmitting}
          />
        )}
      </PropertyPill>
    );

    const priorityPill = (
      <PropertyPill
        icon={priorityIcon}
        iconClassName={priorityIconClass}
        label={t('task.createForm.pill.priority')}
        value={priorityValue}
        disabled={isSubmitting}
        popoverTitle={t('task.field.priority')}
      >
        {(close) => (
          <PillOptionList
            ariaLabel={t('task.field.priority')}
            value={form.priority}
            options={taskPriorityOptions.map((option) => ({
              value: option.value,
              label: t(option.labelKey),
              icon: getPriorityIconPath(option.value),
              iconClassName: getPriorityColor(option.value),
            }))}
            onSelect={(value) => {
              setField('priority', value);
              close();
            }}
          />
        )}
      </PropertyPill>
    );

    const assigneePill = (
      <PropertyPill
        icon={CREATE_ICONS.user}
        label={t('task.createForm.pill.assignee')}
        value={form.assigneeName}
        required={capabilities.requiresAssignee}
        invalid={Boolean(errors.assignee)}
        errorId="task-quick-create-assignee-error"
        disabled={isSubmitting}
        popoverTitle={t('task.field.assignee')}
        popoverWidth={300}
        data-field-error={errors.assignee ? 'true' : undefined}
      >
        {() => (
          <>
            <UserPicker
              value={form.assignee}
              onChange={setAssignee}
              placeholder={t('task.field.assigneePlaceholder')}
              scope={pickerScope}
            />
            {form.assignee && (
              <button
                type="button"
                onClick={() => setAssignee('', '')}
                className={clearButtonClass}
              >
                {t('common.clear')}
              </button>
            )}
          </>
        )}
      </PropertyPill>
    );

    const duePill = (
      <PropertyPill
        icon={CREATE_ICONS.calendar}
        label={t('task.createForm.pill.due')}
        value={formatDate(form.due_date)}
        invalid={Boolean(errors.due_date)}
        errorId="task-quick-create-due-error"
        disabled={isSubmitting}
        popoverTitle={t('task.field.dueDate')}
        popoverWidth={240}
      >
        {() => (
          <div className="flex flex-col gap-2">
            <label htmlFor="task-quick-create-due-date" className="sr-only">
              {t('task.field.dueDate')}
            </label>
            <input
              id="task-quick-create-due-date"
              type="date"
              value={form.due_date}
              onChange={(e) => setField('due_date', e.target.value)}
              className={inputClass}
            />
            {form.due_date && (
              <button
                type="button"
                onClick={() => setField('due_date', '')}
                className={clearButtonClass}
              >
                {t('common.clear')}
              </button>
            )}
          </div>
        )}
      </PropertyPill>
    );

    // Estimate exists only where the type tracks time — a field that would be
    // ignored on save is worse than one that is absent.
    const estimatePill = capabilities.timeTracking && (
      <PropertyPill
        icon={CREATE_ICONS.clock}
        label={t('task.createForm.pill.estimate')}
        value={
          form.estimated_hours
            ? t('task.createForm.hoursShort', { hours: form.estimated_hours })
            : ''
        }
        disabled={isSubmitting}
        popoverTitle={t('task.estimatedHours')}
        popoverWidth={220}
      >
        {() => (
          <div className="flex flex-col gap-2">
            <label htmlFor="task-quick-create-estimate" className="sr-only">
              {t('task.estimatedHours')}
            </label>
            <input
              id="task-quick-create-estimate"
              type="text"
              inputMode="decimal"
              value={form.estimated_hours}
              onChange={(e) => setField('estimated_hours', e.target.value)}
              placeholder="—"
              className={`${inputClass} tabular-nums`}
            />
            {form.estimated_hours && (
              <button
                type="button"
                onClick={() => setField('estimated_hours', '')}
                className={clearButtonClass}
              >
                {t('task.clearEstimatedHours')}
              </button>
            )}
          </div>
        )}
      </PropertyPill>
    );

    const tagsPill = (
      <PropertyPill
        icon={CREATE_ICONS.tag}
        label={t('task.createForm.pill.tags')}
        value={tagsValue}
        disabled={isSubmitting}
        popoverTitle={t('task.field.tags')}
        popoverWidth={300}
      >
        {() => <TagPicker value={tags} onChange={setTags} placeholder={t('tag.selectTags')} />}
      </PropertyPill>
    );

    const parentPill = (
      <PropertyPill
        icon={CREATE_ICONS.folder}
        label={t('task.createForm.pill.parent')}
        value={parentValue}
        disabled={isSubmitting}
        popoverTitle={t('task.field.parent')}
        popoverHint={t('task.createForm.parentDrivesForm', { parent: parentDisplayName })}
        popoverWidth={300}
      >
        {() => (
          <div className="flex flex-col gap-3">
            <PillOptionList
              ariaLabel={t('task.createForm.parentType')}
              value={parentKind}
              options={parentKinds
                // The free-form entity escape hatch is a full-form question:
                // typing a type name and a raw id is not a two-second job.
                .filter((kind) => kind !== 'entity' || parentKind === 'entity')
                .map((kind) => ({
                  value: kind,
                  label: t(`task.createForm.parent.${kind}`),
                }))}
              onSelect={(kind) => {
                touched.current.add('parent');
                setParentKind(kind as ParentKind);
                setParentId('');
                applyTemplate(null);
              }}
            />

            {(parentKind === 'project' || parentKind === 'team') && (
              <div className="border-t border-border pt-2">
                {parentIsLoading ? (
                  <div
                    className="h-8 w-full motion-safe:animate-pulse rounded-md bg-muted"
                    aria-busy="true"
                  />
                ) : parentLoadError ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-xs text-destructive">
                      {t('task.createForm.parentLoadFailed')}
                    </p>
                    <button
                      type="button"
                      onClick={retryParents}
                      className="rounded-sm text-xs font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {t('common.retry')}
                    </button>
                  </div>
                ) : parentOptions.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {t('task.createForm.noParentOptions')}
                  </p>
                ) : (
                  <PillOptionList
                    ariaLabel={t('task.field.parent')}
                    value={parentId}
                    options={parentOptions}
                    onSelect={(value) => {
                      touched.current.add('parent');
                      setParentId(value);
                      applyTemplate(null);
                    }}
                  />
                )}
              </div>
            )}

            {parentKind === 'entity' && (
              <p className="border-t border-border pt-2 text-xs text-muted-foreground">
                {parentValue || notSet}
              </p>
            )}
          </div>
        )}
      </PropertyPill>
    );

    return (
      <>
        <aside
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-labelledby="task-quick-create-heading"
          className="fixed inset-y-0 right-0 z-40 flex w-full flex-col border-l border-border bg-background shadow-2xl sm:w-[400px]"
        >
          {/* Heading: what this is, and the way out. */}
          <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
            <h2 id="task-quick-create-heading" className="text-sm font-semibold text-foreground">
              {t('task.quickCreate.title')}
            </h2>
            <button
              type="button"
              onClick={requestClose}
              aria-label={t('task.quickCreate.close')}
              title={t('task.quickCreate.close')}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Icon d={CREATE_ICONS.x} className="h-4 w-4" />
            </button>
          </header>

          <form
            ref={formRef}
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
              {/* A restored draft explains why the fields are not empty. */}
              {draftRestored && (
                <div role="status" className="flex flex-wrap items-center gap-2 text-xs">
                  <Icon d={CREATE_ICONS.restore} className="h-3.5 w-3.5 shrink-0 text-primary" />
                  <span className="text-muted-foreground">
                    {t('task.createForm.draftRestored')}
                  </span>
                  <button
                    type="button"
                    onClick={discardDraft}
                    className="rounded-sm font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {t('task.createForm.discardDraft')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDraftRestored(false)}
                    aria-label={t('common.close')}
                    className="rounded-sm p-0.5 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Icon d={CREATE_ICONS.x} className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              {/* The key this task will get, when a numbering scheme applies. */}
              <div data-field-error={errors.title ? 'true' : undefined}>
                {keyPreview && (
                  <p
                    className="mb-1 font-mono text-xs text-muted-foreground"
                    title={numberingScheme?.name || t('task.createForm.keyPreview')}
                  >
                    {keyPreview}
                  </p>
                )}
                <TitleInput
                  id={TITLE_FIELD_ID}
                  value={form.title}
                  onChange={(value) => setField('title', value)}
                  placeholder={t('task.createForm.titlePlaceholder')}
                  initialFocus
                  aria-label={t('task.field.title')}
                  onEnter={() => descriptionRef.current?.focus()}
                />
                {errors.title && (
                  <p role="alert" className="mt-1 text-xs text-destructive">
                    {errorText('title')}
                  </p>
                )}
              </div>

              <AutoGrowTextarea
                ref={descriptionRef}
                id="task-quick-create-description"
                value={form.description}
                onChange={(value) => setField('description', value)}
                placeholder={t('task.createForm.descriptionPlaceholder')}
                aria-label={t('task.field.description')}
                minRows={2}
              />

              {/* The properties, as pills — the compact half of the pair. */}
              <div
                className="flex flex-wrap items-center gap-1.5 border-t border-border pt-3"
                role="group"
                aria-label={t('task.createForm.properties')}
              >
                {typePill}
                {priorityPill}
                {assigneePill}
                {duePill}
                {estimatePill}
                {tagsPill}
                {parentPill}
              </div>

              {errorText('assignee') && (
                <p
                  id="task-quick-create-assignee-error"
                  role="alert"
                  className="text-xs text-destructive"
                >
                  {errorText('assignee')}
                </p>
              )}
              {errorText('due_date') && (
                <p
                  id="task-quick-create-due-error"
                  role="alert"
                  className="text-xs text-destructive"
                >
                  {errorText('due_date')}
                </p>
              )}

              {/* Offers: only what this type actually enables. */}
              <div className="flex flex-wrap items-center gap-1 border-t border-border pt-3">
                {capabilities.checklists && (
                  <AddAction
                    icon={CREATE_ICONS.checklist}
                    label={t('task.checklists')}
                    count={checklistItemCount}
                    open={sectionOpen(SECTION_CHECKLISTS, checklists.length > 0)}
                    onToggle={() => toggleSection(SECTION_CHECKLISTS, checklists.length > 0)}
                    controls="task-quick-create-section-checklists"
                    disabled={isSubmitting}
                  />
                )}
                {capabilities.watchers && (
                  <AddAction
                    icon={CREATE_ICONS.users}
                    label={t('task.watchers')}
                    count={watchers.length}
                    open={sectionOpen(SECTION_WATCHERS, watchers.length > 0)}
                    onToggle={() => toggleSection(SECTION_WATCHERS, watchers.length > 0)}
                    controls="task-quick-create-section-watchers"
                    disabled={isSubmitting}
                  />
                )}
                <AddAction
                  icon={CREATE_ICONS.paperclip}
                  label={t('task.attachments')}
                  count={pendingFiles.length}
                  open={sectionOpen(SECTION_ATTACHMENTS, pendingFiles.length > 0)}
                  onToggle={() => toggleSection(SECTION_ATTACHMENTS, pendingFiles.length > 0)}
                  controls="task-quick-create-section-attachments"
                  disabled={isSubmitting}
                />
                {requiredCustomFieldGroups.map((group) => {
                  const key = customFieldSectionKey(group);
                  return (
                    <AddAction
                      key={key}
                      icon={CREATE_ICONS.sliders}
                      label={group.key || t('task.customFields')}
                      count={filledInGroup(group)}
                      open={sectionOpen(key, true)}
                      onToggle={() => toggleSection(key, true)}
                      controls={`task-quick-create-section-${key}`}
                      disabled={isSubmitting}
                    />
                  );
                })}
              </div>

              {capabilities.checklists && sectionOpen(SECTION_CHECKLISTS, checklists.length > 0) && (
                <CreateSection
                  id="task-quick-create-section-checklists"
                  title={t('task.checklists')}
                >
                  <ChecklistDraftEditor checklists={checklists} onChange={setChecklists} />
                </CreateSection>
              )}

              {capabilities.watchers && sectionOpen(SECTION_WATCHERS, watchers.length > 0) && (
                <CreateSection id="task-quick-create-section-watchers" title={t('task.watchers')}>
                  {watchers.length > 0 && (
                    <ul className="mb-2 flex flex-wrap gap-1.5">
                      {watchers.map((watcher) => (
                        <li
                          key={watcher.id}
                          className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs text-foreground"
                        >
                          <span
                            className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[8px] font-medium text-white"
                            style={{ backgroundColor: getAvatarColor(watcher.name) }}
                            aria-hidden="true"
                          >
                            {getInitials(watcher.name)}
                          </span>
                          {watcher.name}
                          <button
                            type="button"
                            onClick={() =>
                              setWatchers((prev) => prev.filter((w) => w.id !== watcher.id))
                            }
                            aria-label={t('task.createForm.removeWatcher', { name: watcher.name })}
                            className="rounded-sm text-muted-foreground hover:text-destructive focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <Icon d={CREATE_ICONS.x} className="h-3 w-3" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <UserPicker
                    value=""
                    onChange={(userId, displayName) => {
                      if (!userId || watchers.some((w) => w.id === userId)) return;
                      setWatchers((prev) => [...prev, { id: userId, name: displayName }]);
                    }}
                    placeholder={t('task.addWatcher')}
                    scope={pickerScope}
                  />
                </CreateSection>
              )}

              {sectionOpen(SECTION_ATTACHMENTS, pendingFiles.length > 0) && (
                <CreateSection
                  id="task-quick-create-section-attachments"
                  title={t('task.attachments')}
                >
                  <AttachmentDropzone
                    files={pendingFiles}
                    onAdd={handleAddFiles}
                    onRemove={(id) => setPendingFiles((prev) => prev.filter((file) => file.id !== id))}
                    disabled={isSubmitting}
                  />
                </CreateSection>
              )}

              {requiredCustomFieldGroups.map((group) => {
                const key = customFieldSectionKey(group);
                if (!sectionOpen(key, true)) return null;
                return (
                  <CreateSection
                    key={key}
                    id={`task-quick-create-section-${key}`}
                    title={group.key || t('task.customFields')}
                  >
                    <div className="flex flex-col gap-3">
                      {group.fields
                        .filter(
                          (def) =>
                            def.is_required || !isCustomFieldEmpty(cfValues[customFieldId(def)]),
                        )
                        .map((def) => {
                          const fieldId = customFieldId(def);
                          const domId = `task-quick-create-cf-${fieldId}`;
                          const message = errorText(fieldId);
                          const labelable =
                            def.field_type !== 'CUSTOM_FIELD_TYPE_USER' &&
                            def.field_type !== 'CUSTOM_FIELD_TYPE_MULTI_SELECT';
                          return (
                            <div key={fieldId} data-field-error={message ? 'true' : undefined}>
                              <label
                                htmlFor={labelable ? domId : undefined}
                                className="mb-1 block text-xs text-muted-foreground"
                              >
                                {def.display_name}
                                {def.is_required && (
                                  <span className="ml-1 text-destructive" aria-hidden="true">
                                    *
                                  </span>
                                )}
                              </label>
                              <CustomFieldControl
                                def={def}
                                id={domId}
                                value={cfValues[fieldId]}
                                error={message}
                                projectId={parentKind === 'project' ? parentId : undefined}
                                teamId={parentKind === 'team' ? parentId : undefined}
                                onChange={(value, label) => setCustomField(fieldId, value, label)}
                              />
                            </div>
                          );
                        })}
                    </div>
                  </CreateSection>
                );
              })}

              {/* Anything that went wrong, or that this path cannot carry. */}
              {contextError && (
                <Alert variant="warning">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="flex-1">{t('task.createForm.contextDegraded')}</span>
                    <button
                      type="button"
                      onClick={retryContext}
                      className="rounded-md px-2 py-1 text-sm font-medium underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {t('common.retry')}
                    </button>
                  </div>
                </Alert>
              )}
              {templateDropped.length > 0 && (
                <Alert variant="warning">
                  {t('task.createForm.templateDrops', { fields: templateDropped.join(', ') })}
                </Alert>
              )}
              {uploadWarning && (
                <Alert variant="warning">{t('task.createForm.attachmentsFailed')}</Alert>
              )}
              {!!store.error && (
                <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />
              )}
            </div>

            {/* The footer: what is still missing, the way to the full form,
                and Create. */}
            <div className="space-y-2 border-t border-border bg-background px-4 py-3">
              {/* What just happened comes first: right after a create the form
                  is empty again, and "the title is missing" would be a
                  complaint about the clean slate we just handed over. */}
              <p className="text-xs" aria-live="polite">
                {justCreated ? (
                  <span className="text-muted-foreground">
                    {t('task.quickCreate.createdKeepGoing', { title: justCreated })}
                  </span>
                ) : missingRequired.length > 0 ? (
                  <span className="text-destructive">
                    {t('task.createForm.requiredRemaining', {
                      count: missingRequired.length,
                      fields: missingRequired.join(', '),
                    })}
                  </span>
                ) : (
                  <span className="text-muted-foreground">{t('task.submitHint')}</span>
                )}
              </p>
              <div className="flex items-center justify-between gap-3">
                <Link
                  to={fullFormHref}
                  onClick={flushDraft}
                  className="inline-flex items-center gap-1.5 rounded-sm text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Icon d={CREATE_ICONS.externalLink} className="h-3.5 w-3.5" />
                  {t('task.quickCreate.openFullForm')}
                </Link>
                <Button type="submit" size="sm" disabled={!canSubmit} isLoading={isSubmitting}>
                  {t('task.create')}
                </Button>
              </div>
            </div>
          </form>
        </aside>

        <ConfirmDialog
          open={confirmClose}
          title={t('task.quickCreate.closeTitle')}
          description={t('task.quickCreate.closeDescription')}
          confirmLabel={t('task.quickCreate.closeConfirm')}
          cancelLabel={t('task.createForm.discardCancel')}
          onConfirm={closeKeepingDraft}
          onCancel={() => setConfirmClose(false)}
        />
      </>
    );
  },
);

export default TaskQuickCreateDrawer;
